<template>
  <LModal
    :visible="visibleEventModal"
    class="issue-modal"
    centered
    size="lg"
    :title="title"
    scrollable
    :observer-name="`${event.id || 'newEventModal'}`"
    :submit="
      async () => {
        $emit('save', false);
        return { success: true };
      }
    "
    @observer:valid="$emit('observer:valid', $event)"
    @update:visible="$emit('update:visibleEventModal', $event)"
    @abort="$emit('abort')"
    @cancel="$emit('cancel')"
  >
    <template #header>
      <slot name="header" />
    </template>

    <template #default="{ submit }">
      <EventModalComponent
        :visible="true"
        :event-type="eventType"
        :issue-type="issueType"
        :event="event"
        v-bind="$attrs"
        @save="submit"
      />
    </template>
    <template #footer="{ close, validation }">
      <div class="text-right">
        <LISAButton class="mr-1" severity="secondary" variant="secondary" @click="close">
          <i class="fas fa-times mr-1" />
          {{ t("global.cancel") }}
        </LISAButton>
        <AccessRightWrapper right="sqdc.write" class="inline">
          <LISAButton
            v-if="!hideAddTaskButton"
            class="mr-1"
            outlined
            variant="success"
            severity="success"
            data-testid="saveAndCreateNewEventButton"
            :disabled="disableEvent || !validation.valid || loading || isEventArchived || isSaving"
            @click="saveEvent(true, validation)"
          >
            <i class="fas fa-save mr-1"></i>
            {{ saveAndNewButtonTitle }}
          </LISAButton>
          <LISAButton
            variant="success"
            severity="success"
            data-testid="saveEventButton"
            :disabled="
              !validation.valid || loading || disableEventInput || isEventArchived || isSaving
            "
            @click="saveEvent(false, validation)"
          >
            <i class="fas fa-save mr-1"></i>
            {{ create ? t("components.issueModal.create") : t("components.issueModal.update") }}
          </LISAButton>
        </AccessRightWrapper>
      </div>
    </template>
  </LModal>
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import { computed, type PropType, provide, ref, watch } from "vue";
import { useStore } from "vuex";
import LISAButton from "@LISAPrime/LISAButton.vue";
import injectAsserted from "@/functions/assert/injectAsserted";
import { EventType, IssueType } from "@/graphql";
import { CreateInjectionKey, DisableEventInjectionKey } from "../../issue/IssueTypes";
import EventModalComponent from "./EventModalComponent.vue";
import AccessRightWrapper from "@/components/AccessRightWrapper.vue";
import LModal from "@/components/LModal.vue";
import type { EventEditType } from "./EventEditType";
import type { FormValidationController } from "@/modules/formValidation";

defineOptions({
  inheritAttrs: true,
});

const props = defineProps({
  visibleEventModal: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    required: true,
  },
  saveAndNewButtonTitle: {
    type: String,
    required: true,
  },
  hideAddTaskButton: {
    type: Boolean,
    default: false,
  },
  event: {
    type: Object as PropType<EventEditType>,
    required: true,
  },
  eventType: {
    type: String as PropType<EventType>,
    required: true,
  },
  issueType: {
    type: String as PropType<IssueType>,
    default: null,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  originalOwnerId: {
    type: Number,
    required: false,
    default: null,
  },
  setVisible: {
    type: Boolean,
    default: false,
  },
  disableEventInput: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(["update:visibleEventModal", "save", "abort", "cancel", "observer:valid"]);

const store = useStore();
const create = injectAsserted(CreateInjectionKey);
const isSaving = ref(false);

const isEventArchived = computed(() => props.event.deletedAt != null);

const disableEvent = computed<boolean>(() => {
  return (
    !create.value &&
    ((!store.getters["login/hasAccessRight"]("sqdc.issueChange") &&
      store.getters["login/userId"] !== props.originalOwnerId) ||
      props.disableEventInput ||
      isEventArchived.value)
  );
});

provide(DisableEventInjectionKey, disableEvent);

async function saveEvent(v: boolean, validation: FormValidationController) {
  isSaving.value = true;
  await validation.commitAllBaselines();
  emit("save", Boolean(v));
}

watch(
  () => props.visibleEventModal,
  () => {
    isSaving.value = false;
  },
  {
    immediate: true,
  },
);

const eventRef = computed(() => props.event);
defineExpose({
  saveEvent,
  isEventArchived,
  disableEvent,
  event: eventRef.value,
});

const { t } = useTranslation();
</script>

<style lang="scss" scoped>
.toolTip {
  text-align: start;
  margin-left: -1.5rem;
  margin-bottom: 0;
}
</style>
